import {
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import type { RouteEpisodeOption } from '../../services/faxRouting';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { NEW_TASK, type RouteDestination } from './useRouteDestination';

interface RouteDestinationFieldsProps {
  destination: RouteDestination;
  disabled?: boolean;
}

function episodeLabel(episode: RouteEpisodeOption): string {
  const range = `${formatDateToMMDDYYYY(episode.start_date)} to ${formatDateToMMDDYYYY(episode.end_date)}`;
  return episode.is_current ? `${range} (current)` : range;
}

/** The episode and task a fax would be filed to, or a new task. */
export default function RouteDestinationFields({
  destination: route,
  disabled = false,
}: RouteDestinationFieldsProps) {
  const { episodes, taskList, loading, chartLocked } = route;
  return (
    <div className='flex flex-col gap-4'>
      <div className='space-y-1.5'>
        <Label htmlFor='route-episode'>Episode</Label>
        <Select
          value={route.episodeId}
          onValueChange={route.setEpisodeId}
          disabled={disabled || episodes.length === 0}
        >
          <SelectTrigger id='route-episode' aria-label='Episode'>
            <SelectValue
              placeholder={
                loading && episodes.length === 0 ? 'Loading' : 'No episodes'
              }
            />
          </SelectTrigger>
          <SelectContent>
            {episodes.map(episode => (
              <SelectItem key={episode.id} value={episode.id}>
                {episodeLabel(episode)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {chartLocked && (
        <p className='text-sm text-muted-foreground'>
          This episode&apos;s chart is locked, so nothing can be filed to it.
        </p>
      )}

      {taskList && !chartLocked && (
        <div className='space-y-1.5'>
          <Label>File to task</Label>
          <RadioGroup
            value={route.destination}
            onValueChange={route.setDestination}
            disabled={disabled}
            className='max-h-64 gap-2 overflow-y-auto'
          >
            {taskList.tasks.map(task => (
              <label
                key={task.id}
                className='flex items-start gap-3 rounded border p-2 text-sm'
              >
                <RadioGroupItem
                  value={task.id}
                  disabled={!task.accepts_documents}
                  aria-label={task.display_name}
                />
                <span className='flex min-w-0 flex-col'>
                  <span className='font-medium'>{task.display_name}</span>
                  <span className='text-xs text-muted-foreground'>
                    {formatDateToMMDDYYYY(task.task_date)}
                    {task.document_count === 1
                      ? ', 1 document'
                      : `, ${task.document_count} documents`}
                    {!task.accepts_documents && ', signed'}
                  </span>
                </span>
              </label>
            ))}
            <label className='flex items-start gap-3 rounded border p-2 text-sm'>
              <RadioGroupItem value={NEW_TASK} aria-label='New task' />
              <span className='font-medium'>New task</span>
            </label>
          </RadioGroup>
          {taskList.suggestion.hint && (
            <p className='text-xs text-muted-foreground'>
              {taskList.suggestion.hint}
            </p>
          )}
        </div>
      )}

      {taskList && !chartLocked && route.destination === NEW_TASK && (
        <div className='grid gap-3 sm:grid-cols-2'>
          <div className='space-y-1.5'>
            <Label htmlFor='route-task-type'>Task type</Label>
            <Select
              value={route.newType}
              onValueChange={route.setNewType}
              disabled={disabled}
            >
              <SelectTrigger id='route-task-type' aria-label='Task type'>
                <SelectValue placeholder='Select' />
              </SelectTrigger>
              <SelectContent>
                {taskList.misc_task_types.map(type => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='route-task-name'>Task name (optional)</Label>
            <Input
              id='route-task-name'
              maxLength={120}
              value={route.newName}
              disabled={disabled}
              onChange={event => route.setNewName(event.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
